import React, { useMemo } from 'react';
import { AxiosError } from 'axios';
import { createContext, useContext } from 'use-context-selector';

import { FEATURE_FLAGS } from '~common';
import {
  useQueryGetPaymentInfo,
  useQueryGetUserPaymentMethods,
} from '~hooks/payment';
import { CampusCardSummary, PaymentInfo } from '~types';
import { checkEnabledFeatureFlag } from '~utils';

type UserPaymentContextType = {
  /**
   * The user's saved Stripe payment methods. This is the source of truth;
   * empty when the user has none.
   */
  paymentMethods: PaymentInfo[];
  /**
   * Backward-compatible default/active payment method — the first saved method
   * (the backend returns them ordered by last-rented). Derived from
   * `paymentMethods`.
   */
  paymentInfo: PaymentInfo | null;
  isFetchingPaymentInfo: boolean;
  /**
   * Just with cautions, return false when query enabled is false
   */
  isFetchedPaymentInfo: boolean;
  userCampusCards: CampusCardSummary[];
};

const UserPaymentContext = createContext<UserPaymentContextType>({
  paymentMethods: [],
  paymentInfo: null,
  isFetchingPaymentInfo: false,
  isFetchedPaymentInfo: false,
  userCampusCards: [],
});

type Props = {
  children: React.ReactNode;
};

export const UserPaymentProvider: React.FC<Props> = ({ children }) => {
  const isCampusCardEnabled = checkEnabledFeatureFlag(
    FEATURE_FLAGS.CAMPUS_CARD_BY_WEB_PLATFORM,
  );
  const isMultiCardsPerUserEnabled = checkEnabledFeatureFlag(
    FEATURE_FLAGS.MULTI_CARDS_PER_USER_BY_WEB_PLATFORM,
  );

  // The new `payment-methods` list API backs both the campus-card flow and the
  // multi-card flow; the legacy single-object `info` API is used only when
  // neither is enabled.
  const shouldUseNewPaymentMethodsApi =
    isCampusCardEnabled || isMultiCardsPerUserEnabled;

  const paymentInfoQuery = useQueryGetPaymentInfo({
    enabled: !shouldUseNewPaymentMethodsApi,
  });
  const userPaymentMethodsQuery = useQueryGetUserPaymentMethods({
    enabled: shouldUseNewPaymentMethodsApi,
  });

  const paymentMethods = useMemo<PaymentInfo[]>(() => {
    if (shouldUseNewPaymentMethodsApi) {
      return userPaymentMethodsQuery.data?.paymentMethod ?? [];
    }
    // Legacy single-object API — a missing method surfaces as a 404.
    if (
      paymentInfoQuery.error instanceof AxiosError &&
      paymentInfoQuery.error.response?.status === 404
    ) {
      return [];
    }
    // Normalize the single object into a list so consumers share one contract.
    return paymentInfoQuery.data ? [paymentInfoQuery.data] : [];
  }, [
    shouldUseNewPaymentMethodsApi,
    userPaymentMethodsQuery.data,
    paymentInfoQuery.data,
    paymentInfoQuery.error,
  ]);

  // Backward-compatible default/active method: the first saved method.
  const paymentInfo = useMemo<PaymentInfo | null>(
    () => paymentMethods[0] ?? null,
    [paymentMethods],
  );

  const userCampusCards = useMemo(() => {
    if (!isCampusCardEnabled) {
      return [];
    }
    return userPaymentMethodsQuery.data?.campusCards ?? [];
  }, [isCampusCardEnabled, userPaymentMethodsQuery.data]);

  const isFetchingPaymentInfo = shouldUseNewPaymentMethodsApi
    ? userPaymentMethodsQuery.isFetching
    : paymentInfoQuery.isFetching;
  const isFetchedPaymentInfo = shouldUseNewPaymentMethodsApi
    ? userPaymentMethodsQuery.isFetched
    : paymentInfoQuery.isFetched;

  const value = useMemo(
    () => ({
      paymentMethods,
      paymentInfo,
      isFetchingPaymentInfo,
      isFetchedPaymentInfo,
      userCampusCards,
    }),
    [
      paymentMethods,
      paymentInfo,
      isFetchingPaymentInfo,
      isFetchedPaymentInfo,
      userCampusCards,
    ],
  );

  return (
    <UserPaymentContext.Provider value={value}>
      {children}
    </UserPaymentContext.Provider>
  );
};

export const useUserPaymentContext = () => useContext(UserPaymentContext);
